import { AssetSubClassCode } from '@api/api/entity-constants';
import { Entity, PrimaryMethod } from '@api/api/entity-schema';
import {
  DisplayFormat,
  SnakeMeta,
  TimeSeriesData,
} from '@api/api/timeseries-schema';
import { useQuery } from '@tanstack/react-query';
import {
  fetchSeriesHistory,
  fetchSnakeMeta,
} from '@terminal/services/overview-widget/overview-widget-service';
import { postFilteredTimeSeries } from '@terminal/services/timeseries/timeseries-service';

// Exact economic methods, not display-name matching or country-specific mappings.
export const GDP_METHODS = [
  'gdp_yoy_actual',
  'gdp_qoq_actual',
  'gdp_flash_yoy_actual',
  'gdp_flash_qoq_actual',
  'gdp_level.seas_adj.change_pct(horiz=1y)',
] as const;

export const isVerifiedGdpMeta = (
  tag: string,
  meta?: SnakeMeta
): meta is SnakeMeta =>
  !!meta &&
  meta.entity === tag &&
  meta.active &&
  meta.display_format === DisplayFormat.PERCENT &&
  GDP_METHODS.some(method => meta.snake === `${tag}.${method}`);

export const hasGdpHistory = (
  tag: string,
  snake: string,
  data?: TimeSeriesData
) => {
  if (data?.meta.entity !== tag || data.meta.snake !== snake) {
    return false;
  }

  return data?.result.data?.length >= 2;
};

const getGdpCandidates = (tag: string, metadata: SnakeMeta[]) =>
  GDP_METHODS.flatMap(method => {
    const meta = metadata.find(
      item => item.snake === `${tag}.${method}` && isVerifiedGdpMeta(tag, item)
    );
    return meta ? [meta] : [];
  });

export const discoverCountryGdp = async (tag: string, signal: AbortSignal) => {
  const canonical = `${tag}.gdp_level.seas_adj.change_pct(horiz=1y)`;
  const [discovery, probe] = await Promise.allSettled([
    postFilteredTimeSeries({ entities: [tag] }, signal),
    fetchSnakeMeta(canonical, signal),
  ]);
  signal.throwIfAborted();
  const metadata = discovery.status === 'fulfilled' ? [...discovery.value] : [];
  if (probe.status === 'fulfilled' && probe.value?.snake === canonical) {
    metadata.push(probe.value);
  }
  const candidates = getGdpCandidates(tag, metadata);
  let failure = discovery.status === 'rejected' ? discovery.reason : undefined;
  if (probe.status === 'rejected' && probe.reason?.status !== 404) {
    failure = probe.reason;
  }
  for (const meta of candidates) {
    try {
      const history = await fetchSeriesHistory(meta.snake, signal);
      signal.throwIfAborted();
      if (hasGdpHistory(tag, meta.snake, history)) {
        return meta;
      }
    } catch (error) {
      failure = error;
      signal.throwIfAborted();
    }
  }
  if (failure) {
    throw failure;
  }
  return null;
};

export const useCountryGdp = (entity: Entity) => {
  const result = useQuery({
    queryKey: ['COUNTRY_GDP', entity.tag],
    queryFn: ({ signal }) => discoverCountryGdp(entity.tag, signal),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const snakeMeta = result.data ?? undefined;
  return {
    isSeriesLoading: result.isLoading,
    snakeMeta,
    entity: {
      ...entity,
      default_snake: snakeMeta?.snake ?? '',
      primary_method: PrimaryMethod.GdpLevel,
    },
  };
};

export const isCountryOrArea = (entity?: Pick<Entity, 'sub_class'>) =>
  entity?.sub_class === AssetSubClassCode.EcoCountry ||
  entity?.sub_class === AssetSubClassCode.EcoArea;
